import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_CONFIG } from '../constants/api.constants';
import { Contract, ContractsResponse } from '../interfaces/contract.interface';
import {
  ContractField,
  ContractFieldsResponse,
  ProcessContractDataRequest,
  ContractProcessingResult,
  RetryFailedCompaniesRequest
} from '../interfaces/contract-import.interface';

@Injectable({
  providedIn: 'root'
})
export class ContractsService {
  private apiUrl = `${API_CONFIG.BASE_URL}/auth/contracts`;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    let token = null;
    if (typeof localStorage !== 'undefined') {
      token = localStorage.getItem('token');
    }
    
    const headers: any = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return new HttpHeaders(headers);
  }

  /**
   * Szerződések lekérése az adatbázisból
   */
  getContracts(): Observable<Contract[]> {
    return this.http.get<ContractsResponse>(`${API_CONFIG.BASE_URL}/auth/contracts/`, { 
      headers: this.getHeaders() 
    }).pipe(
      map(response => {
        return response.contracts || [];
      }),
      catchError(error => {
        console.error('HTTP Error occurred:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * Szerződés mezők lekérése
   */
  getContractFields(): Observable<any> {
    return this.http.get(`${this.apiUrl}/fields`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(error => {
        console.error('Hiba a szerződés mezők lekérése során:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Szerződések lekérése adott ügyfél vagy cég ID alapján
   */
  getContractsByEntity(entityType: 'customer' | 'company', entityId: number): Observable<Contract[]> {
    return this.http.get<ContractsResponse>(`${API_CONFIG.BASE_URL}/auth/contracts/by-entity/${entityType}/${entityId}`, { 
      headers: this.getHeaders() 
    }).pipe(
      map(response => {
        return response.contracts || [];
      }),
      catchError(error => {
        console.error('HTTP Error occurred:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * Szerződés mezők lekérése importhoz (moved from contract.service.ts)
   */
  getContractFieldsForImport(): Observable<ContractFieldsResponse> {
    return this.http.get<ContractFieldsResponse>(
      `${API_CONFIG.BASE_URL}/auth/contracts/fields`,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Hiba a szerződés mezők lekérése során:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Szerződés adatok importálása (moved from contract.service.ts)
   */
  importContractData(request: ProcessContractDataRequest): Observable<ContractProcessingResult> {
    return this.http.post<ContractProcessingResult>(
      `${API_CONFIG.BASE_URL}/auth/contracts/import`,
      request,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Hiba a szerződés adatok importálása során:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Sikertelen cégek újrapróbálása (moved from contract.service.ts)
   */
  retryFailedCompanies(request: RetryFailedCompaniesRequest): Observable<ContractProcessingResult> {
    return this.http.post<ContractProcessingResult>(
      `${API_CONFIG.BASE_URL}/auth/contracts/retry-failed`,
      request,
      { headers: this.getHeaders() }
    ).pipe(
      catchError(error => {
        console.error('Hiba a sikertelen cégek újrapróbálása során:', error);
        return throwError(() => error);
      })
    );
  }
}